import { Injectable } from "@angular/core";
import {
  BlotterDefinitionPromiseLookupService,
  BlotterFilterPromiseLookupService,
  UserPromiseLookupService,
  PromiseLookupService,
  ProfilePromiseActionService,
  ProfileSettingKeys,
  AlgoStrategyPromiseLookupService,
  AlgoHedgeConfigService,
  TraderGroupLookupService,
  DIFilterReason,
  RiskTradeTableDefinitionFactory,
} from "shared/cubiq-services";
import {
  BlotterDefinitionImpl,
  BlotterFilterImpl,
  UserImpl,
  BlotterType,
  AssetClass,
  ModelUpdateImpl,
  QueryGroupImpl,
  QueryRuleImpl,
  Condition,
} from "shared/cubiq-model-multi-asset";
import {
  DATE_COLUMN_FILTER_TYPES,
  NUMBER_COLUMN_FILTER_TYPES,
  OBJECT_COLUMN_FILTER_TYPES,
  BOOLEAN_COLUMN_FILTER_TYPES,
  BFXBLOTTERFILTERCONDITIONLABELS,
  COMPOSITE_COLUMN_FILTER_TYPES,
  ExtendedOrderTypeIdentifier,
} from "./models/bfx-blotter-filter.constants";
import {
  OrdersTableDefinitionFactory,
  TradeTableDefinitionFactory,
  InterventionTableDefinitionFactory,
  TableDefinition,
  ColumnDefinition,
  ActiveUserProfilePromiseService,
  BmarkClientOrdersTableDefinitionFactory,
} from "shared/cubiq-services";
import { TranslateService } from "@ngx-translate/core";
import { Subject, Observable, map } from "rxjs";
import { BfxBlotterFilterSelectorService } from "./bfx-blotter-filter-selector.service";
import { DatePipe } from "@angular/common";
import { BfxDialogData } from "../bfx-dialogs/models/bfx-dialog-data.model";
import { BfxDialogCommand } from "../bfx-dialogs/models/bfx-dialog-command.enum";
import {
  ActiveUser,
  SystemConfigPromiseService,
  SysFeatures,
  AutoCompleteFilterOrderTypeLabelValue,
  FilterOrderTypes,
} from "shared/cubiq-services";
import { BfxUserProfileService } from "../bfx-common/services/bfx-user-profile.service";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { CURRENCY_GROUP_VALUE_IDENTIFIER } from "../bfx-base-blotter/bfx-base-blotter-filtering.service";
import { GoldenLayoutService } from "../golden-layout/golden-layout-service";
import { BfxBlotterDeleteFilterActionEvent } from "./models/bfx-blotter-filter-delete-event";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BMARK_DEFAULT_NAME } from "shared/cubiq-services/model/BmarkClientOrdersTableDefinitionFactory.constants";
import {
  BfxBlotterFilterId,
  FilterType,
  BfxBlotterFilter,
  EXTENDED_TYPE_PROPERTY_NAME,
  BfxBlotterNewFilterActionEvent,
  BfxBlotterFilterCondition,
  BfxBlotterFilterActionEvent,
} from "./bfx-blotter-filter.constants";
import { AlgoMonitorDefinitionFactory } from "shared/cubiq-services/model/AlgoMonitorDefinitionFactory";
import { FilterConfigurationEvent, FilterConfiguration } from "./FilterConfiguration";

@Injectable()
export class BfxBlotterFilterService {
  protected definitionsMap: any;
  public modelUpdateSubject: Subject<ModelUpdateImpl> = new Subject<ModelUpdateImpl>();
  public filterFavoriteSubject: Subject<any> = new Subject<any>();
  protected filterConfigurationSubject: Subject<FilterConfigurationEvent> =
    new Subject<FilterConfigurationEvent>();
  public filterConfiguration$: Observable<FilterConfigurationEvent> =
    this.filterConfigurationSubject.pipe(
      map((filterConfiguration) => {
        filterConfiguration.items = this.migrateFilterConfiguration(filterConfiguration.items);
        return filterConfiguration;
      }),
    );
  public resizeSubject: Subject<void> = new Subject<void>();
  public isEditableMode = new Subject<boolean>();
  isEdit$ = this.isEditableMode.asObservable();
  protected _currencyPairGroupData: Array<any> = [];
  private filtersDeletedSubject: Subject<string[]> = new Subject();
  public isDialogOpenMode = new Subject<boolean>();
  isDialogOpen$ = this.isDialogOpenMode.asObservable();

  public betweenOperators: any = {};
  public multipleOperators: any = {};

  public filterViewComponents: any = {};
  public filterManagerComponents: any = {};
  public filterListComponents: any = {};

  private activeUser: ActiveUser;
  private copyFromBlotterType: BlotterType;
  // FIXME: Initialise with DI

  // Observable string sources
  private filterActionSubscription = new Subject<BfxDialogData>();
  // Observable string streams
  public dialogCtrl$ = this.filterActionSubscription.asObservable();

  public _selFilter: string = "allFilters";

  constructor(
    protected definitionService: BlotterDefinitionPromiseLookupService,
    protected filterService: BlotterFilterPromiseLookupService,
    protected activeUserService: ActiveUserProfilePromiseService,
    protected userService: UserPromiseLookupService,
    protected translationService: TranslateService,
    protected ordersDefinition: OrdersTableDefinitionFactory,
    protected tradesDefinition: TradeTableDefinitionFactory,
    protected interventionDefinition: InterventionTableDefinitionFactory,
    protected clientOrdersDefinition: BmarkClientOrdersTableDefinitionFactory,
    protected algoMonitorDefinition: AlgoMonitorDefinitionFactory,
    protected riskTradeDefinition: RiskTradeTableDefinitionFactory,
    protected profileService: ProfilePromiseActionService,
    protected selectorService: BfxBlotterFilterSelectorService,
    protected datePipe: DatePipe,
    protected systemConfigService: SystemConfigPromiseService,
    protected goldenLayoutService: GoldenLayoutService,
    protected algoStrategyLookupService: AlgoStrategyPromiseLookupService,
    protected algoHedgeConfigService: AlgoHedgeConfigService,
    private userProfileService: BfxUserProfileService,
    private traderGroupService: TraderGroupLookupService,
  ) {
    this.activeUserService.getActiveUserData().then(
      (activeUser: ActiveUser) => {
        this.activeUser = activeUser;

        this.definitionService
          .getModelUpdateSubject()
          .subscribe((e: ModelUpdateImpl) => this.modelUpdateSubject.next(e));
        this.filterService
          .getModelUpdateSubject()
          .subscribe((e: ModelUpdateImpl) => this.modelUpdateSubject.next(e));
        this.userService.getModelUpdateSubject().subscribe((e: ModelUpdateImpl) => {
          let user: CubiqTS.UserImpl = e.payLoad instanceof CubiqTS.UserImpl ? e.payLoad : null;

          if (user !== null && user.userCode !== activeUser.userCode) {
            // FXBFXOM-440 theme change was triggering re-apply of filters
            // skip updates to own user object - as updates will not impact filtering
            // while css was being modified
            this.modelUpdateSubject.next(e);
          }
        });
      },
      (reason) => {
        logger.out.error(`BfxBlotterOrdersContextMenuService: ActiveUser ${reason}`);
      },
    );

    BFXBLOTTERFILTERCONDITIONLABELS.forEach((operator: any) => {
      let multiple: boolean =
        operator.value == CubiqTS.Operator.In ||
        operator.value == CubiqTS.Operator.NotIn ||
        operator.value == CubiqTS.Operator.Equal ||
        operator.value == CubiqTS.Operator.NotEqual;
      this.multipleOperators[operator.value] = multiple;
      let between: boolean =
        operator.value == CubiqTS.Operator.Between || operator.value == CubiqTS.Operator.NotBetween;
      this.betweenOperators[operator.value] = between;
    });
  }

  public getFavoriteFilters(
    blotterTypeId: BfxBlotterFilterId,
    extendedTypeId: ExtendedOrderTypeIdentifier,
  ): Promise<Array<string>> {
    let profileConfigKey: string = this.setFavoritesFiltersProfileKey(
      blotterTypeId,
      extendedTypeId,
    );
    profileConfigKey = profileConfigKey.toLowerCase();

    return new Promise<Array<string>>((res, rej) => {
      this.profileService.getProfileConfig(profileConfigKey).then(
        (values: string) => {
          let ids = values != null && values.length > 0 ? JSON.parse(values) : [];
          ids = ids.map((id) => (typeof id === "number" ? `${id}` : id));
          res(ids);
        },
        () => {
          res([]);
        },
      );
    });
  }

  public setFavoriteFilters(
    favoriteFilterIds: Array<string>,
    blotterTypeId: BfxBlotterFilterId,
    extendedTypeId: ExtendedOrderTypeIdentifier,
  ): void {
    let profileConfigKey: string = this.setFavoritesFiltersProfileKey(
      blotterTypeId,
      extendedTypeId,
    );
    let profileSettings: Array<CubiqTS.ProfileSettingImpl> = [];
    profileSettings.push(
      this.userProfileService.createProfileSettingObject(
        profileConfigKey.toLowerCase(),
        JSON.stringify(favoriteFilterIds),
      ),
    );
    this.profileService.updateProfile(profileSettings, false).then(() => {
      this.filterFavoriteSubject.next({
        ids: favoriteFilterIds,
        blotterTypeId: blotterTypeId,
        ...(extendedTypeId != null && { extendedTypeId: extendedTypeId }),
      });
    });
  }

  private setFavoritesFiltersProfileKey(
    blotterTypeId: BfxBlotterFilterId,
    extendedTypeId: ExtendedOrderTypeIdentifier,
  ): string {
    let profileConfigKey =
      ProfileSettingKeys.filterFavourites +
      BlotterType[blotterTypeId.blotterType] +
      "-" +
      AssetClass[blotterTypeId.assetClass];
    if (
      blotterTypeId.blotterType === BlotterType.ORDER &&
      extendedTypeId === ExtendedOrderTypeIdentifier.BENCHMARK &&
      extendedTypeId != null
    ) {
      return profileConfigKey + "-" + extendedTypeId;
    }
    return profileConfigKey;
  }
  public getItems(
    type: FilterType,
    blotterTypeId: BfxBlotterFilterId = null,
    extendedTypeId?: ExtendedOrderTypeIdentifier,
  ): Promise<Array<BfxBlotterFilter>> {
    let service: PromiseLookupService<any, any> =
      type == FilterType.BlotterDefinition ? this.definitionService : this.filterService;

    return new Promise<Array<BfxBlotterFilter>>((res, rej) => {
      service.isPrimed().then(() => {
        service.getAll().then(async (items: Array<any>) => {
          if (blotterTypeId != null) {
            items = items.filter(
              (i) =>
                i.blotterType == blotterTypeId.blotterType &&
                i.assetClass == blotterTypeId.assetClass &&
                this.filterOnExtendedTypeId(i, extendedTypeId),
            );
          }

          if (this.activeUser == Object(this.activeUser) && this.activeUser.userTypeId != null) {
            // if blotterfilter has a defined username, remove ones which do not match our userType
            items = items.filter((i) =>
              i.userType ? this.activeUser.userTypeId == i.userType : true,
            );
          }

          let userIds: Set<string> = this.getUserIds(items);
          let userMap = await this.getUserMap(userIds);

          res(items.map((i) => BfxBlotterFilterService.transform(i, userMap)));
        });
      });
    });
  }

  public getBlotterFilter(id: any): Promise<BfxBlotterFilter> {
    return new Promise<BfxBlotterFilter>((res, rej) => {
      this.filterService.isPrimed().then(() => {
        this.filterService.getItem(id).then((item: CubiqTS.BlotterFilterImpl) => {
          //transform
          if (item) {
            let extendedTypeId: ExtendedOrderTypeIdentifier = item.extendedInfo.has(
              EXTENDED_TYPE_PROPERTY_NAME,
            )
              ? ExtendedOrderTypeIdentifier[item.extendedInfo.get(EXTENDED_TYPE_PROPERTY_NAME)]
              : null;
            let blotterId: string = item.filterId.toString();
            let filter: BfxBlotterFilter = new BfxBlotterFilter(
              blotterId,
              item.filterName,
              null,
              null,
              null,
            );
            filter.queryGroup = item.queryGroup;
            filter.extendedTypeId = extendedTypeId;
            res(filter);
          } else {
            res(null);
          }
        });
      });
    });
  }

  public getBlotterFilterSync(id: any): BfxBlotterFilter {
    let item: CubiqTS.BlotterFilterImpl = this.filterService.getBase().getItem(id);
    //transform
    if (item) {
      let extendedTypeId: ExtendedOrderTypeIdentifier = item.extendedInfo.has(
        EXTENDED_TYPE_PROPERTY_NAME,
      )
        ? ExtendedOrderTypeIdentifier[item.extendedInfo.get(EXTENDED_TYPE_PROPERTY_NAME)]
        : null;
      let blotterId: string = item.filterId.toString();
      let filter: BfxBlotterFilter = new BfxBlotterFilter(
        blotterId,
        item.filterName,
        null,
        null,
        null,
      );
      filter.queryGroup = item.queryGroup;
      filter.extendedTypeId = extendedTypeId;
      return filter;
    }

    return null;
  }

  public getMyItems(
    type: FilterType,
    blotterTypeId: BfxBlotterFilterId = null,
    extendedTypeId?: ExtendedOrderTypeIdentifier,
  ): Promise<Array<BfxBlotterFilter>> {
    let listCall: any =
      type == FilterType.BlotterDefinition
        ? this.definitionService.getMyBlotterDefinitions
        : this.filterService.getMyBlotterFilters;
    listCall = listCall.bind(
      type == FilterType.BlotterDefinition ? this.definitionService : this.filterService,
    );

    return new Promise<Array<BfxBlotterFilter>>((res, rej) => {
      listCall().then(async (items: Array<BlotterDefinitionImpl>) => {
        if (blotterTypeId != null) {
          items = items.filter(
            (i) =>
              i.blotterType == blotterTypeId.blotterType &&
              i.assetClass == blotterTypeId.assetClass &&
              this.filterOnExtendedTypeId(i, extendedTypeId),
          );
        }

        let userIds: Set<string> = this.getUserIds(items);
        let userMap = await this.getUserMap(userIds);

        res(items.map((i) => BfxBlotterFilterService.transform(i, userMap)));
      });
    });
  }

  public getUserIds(items: BlotterDefinitionImpl[]): Set<string> {
    return items.reduce((ids, item) => {
      let id = CubiqTS.Long.isLong(item?.entryUserId) ? item.entryUserId.toString() ?? null : null;

      if (id != null) ids.add(id);

      return ids;
    }, new Set<string>());
  }

  public async getUserMap(userIds: Set<string>): Promise<Map<string, string>> {
    let userMap = new Map<string, string>();
    let users = await this.userService.getUsers([...userIds]);
    let cfg = await this.systemConfigService.getSystemConfig(SysFeatures.displayUserNames);
    let showUserName = cfg == "true" || cfg == "TRUE" || cfg == "1" || cfg == "Y" || cfg == "y";

    users.forEach((user) => {
      if (user != null && CubiqTS.Long.isLong(user?.userId)) {
        userMap.set(
          user.userId.toString(),
          showUserName === true ? user?.userName : user?.userCode,
        );
      }
    });

    return Promise.resolve(userMap);
  }

  public createFilter(
    type: FilterType,
    name: string,
    blotterType: BlotterType,
    assetClass: AssetClass,
    copied?: boolean,
    extendedTypeId?: ExtendedOrderTypeIdentifier,
  ): Promise<BfxBlotterFilter> {
    return new Promise<BfxBlotterFilter>((res, rej) => {
      let filter: BfxBlotterFilter = new BfxBlotterFilter(null, name, null, null, null, false);
      filter.filterIdentifier = new BfxBlotterFilterId(blotterType, assetClass);
      filter.extendedTypeId = extendedTypeId;
      if (!copied) this.editItem(filter);
      res(filter);
    });
  }

  public saveFilter(
    type: FilterType,
    filter: BfxBlotterFilter,
    queryGroup: QueryGroupImpl,
    copied?: boolean,
    extendedTypeId?: ExtendedOrderTypeIdentifier,
  ): Promise<boolean> {
    let service: any =
      type == FilterType.BlotterDefinition ? this.definitionService : this.filterService;

    let item: any = null;
    if (type == FilterType.BlotterDefinition) {
      let blotterDefinition: BlotterDefinitionImpl = new BlotterDefinitionImpl();
      blotterDefinition.queryGroup = queryGroup;
      blotterDefinition.blotterName = filter.filterName;
      blotterDefinition.blotterType = filter.filterIdentifier.blotterType;
      blotterDefinition.extendedInfo.set(
        EXTENDED_TYPE_PROPERTY_NAME,
        filter.extendedTypeId.toString(),
      );
      item = blotterDefinition;
    } else {
      let blotterFilter: BlotterFilterImpl = new BlotterFilterImpl();
      blotterFilter.queryGroup = queryGroup;
      blotterFilter.filterName = filter.filterName;
      blotterFilter.blotterType = filter.filterIdentifier.blotterType;
      blotterFilter.extendedInfo.set(EXTENDED_TYPE_PROPERTY_NAME, filter.extendedTypeId.toString());
      item = blotterFilter;
    }

    item.filterId = filter.filterId;
    item.blotterId = filter.filterId;

    return new Promise<boolean>((res, rej) => {
      service
        .updateFilterItem(item)
        .then((result: any) => {
          res(result);
          if (copied) {
            this.clearListAndView(
              this.copyFromBlotterType,
              filter.filterIdentifier.assetClass,
              extendedTypeId,
            );
          } else
            this.clearListAndView(
              filter.filterIdentifier.blotterType,
              filter.filterIdentifier.assetClass,
              filter.extendedTypeId,
            );
        })
        .catch((error) => {
          logger.out.error(error);
          rej(false);
        });
    });
  }

  public confirmDeleteFilter(callback: any, body: Element) {
    this.filterActionSubscription.next(
      new BfxDialogData(
        BfxDialogCommand.DIALOG_FILTER_DELETE_OPEN,
        null,
        new BfxBlotterDeleteFilterActionEvent(callback),
        { blotterName: "OrderBlotter" },
        body,
      ),
    );
  }

  public deleteFilters(
    ids: string[],
    blotterType: BlotterType,
    assetClass: AssetClass,
    extendedTypeId: ExtendedOrderTypeIdentifier,
  ): Promise<BfxBlotterFilter> {
    return new Promise<BfxBlotterFilter>((res, rej) => {
      this.filterService
        .deleteItemsByIds(ids)
        .then((result: any) => {
          res(result);

          let filterViewComponent: any = this.getFilterView(
            blotterType,
            assetClass,
            extendedTypeId,
          );
          if (
            filterViewComponent.editedItem != null &&
            ids.indexOf(filterViewComponent.editedItem.filterId) >= 0
          ) {
            filterViewComponent.editedItem = null;
          }
          this.filtersDeletedSubject.next(ids);
        })
        .catch((error) => {
          rej(error.errorMessages);
        });
    });
  }

  public handleErrors(errors: any[], blotterType: BlotterType, assetClass: AssetClass): void {
    let message = "";
    if (errors && Array.isArray(errors)) {
      errors
        .map((e) => JSON.parse(e))
        .forEach((e) => (message += this.translationService.instant(e["id"], e["args"]) + "\n"));
      logger.out.error("BfxBlotterFilterService -->" + message);
    } else {
      logger.out.error("BfxBlotterFilterService --> handleErrors() --> " + errors);
    }
  }

  public initTableDefinition(
    filterId: BfxBlotterFilterId,
    filterType: FilterType,
    blotterId: string = null,
    extendedTypeId?: ExtendedOrderTypeIdentifier,
  ): void {
    this.getTableDefinition(filterId, extendedTypeId)
      .then(
        (tableDef) => {
          let blotterType: BlotterType = filterId.blotterType;
          let assetClass: AssetClass = filterId.assetClass;

          let filterViewComponent: any = this.getFilterView(
            blotterType,
            assetClass,
            extendedTypeId,
          );
          let filterListComponent: any = this.getFilterList(
            blotterType,
            assetClass,
            extendedTypeId,
          );

          try {
            filterViewComponent.initTableDefinition(
              tableDef,
              filterType,
              blotterId,
              extendedTypeId,
            );
            filterListComponent.initTableDefinition(
              tableDef,
              filterType,
              blotterId,
              extendedTypeId,
            );
          } catch (e) {
            logger.out.error(
              "Failed to initialize table definitions on filter view and list components miserably ",
              e,
            );
          }
        },
        (rejection) => {
          logger.out.error(
            "ERROR:BfxBlotterFilterService initTableDefinition rejection:",
            rejection,
          );
        },
      )
      .catch((reason) => {
        logger.out.error("ERROR:BfxBlotterFilterService initTableDefinition reason: ", reason);
      });
  }

  public createNewFilter(
    callback: any,
    reset: boolean = true,
    type: FilterType,
    blotterType: BlotterType,
    body: Element,
    extendedTypeId: ExtendedOrderTypeIdentifier,
  ): void {
    let service: any =
      type == FilterType.BlotterDefinition ? this.definitionService : this.filterService;
    let field: string = type == FilterType.BlotterDefinition ? "blotterName" : "filterName";
    this.filterActionSubscription.next(
      new BfxDialogData(
        BfxDialogCommand.DIALOG_FILTER_NEW_OPEN,
        null,
        new BfxBlotterNewFilterActionEvent(callback, reset, service, field),
        {
          blotterName: "OrderBlotter",
          blotterType: blotterType,
          extendedTypeId: extendedTypeId,
        },
        body,
      ),
    );
  }

  public registerFilterManager(
    filterManager: any,
    blotterType: BlotterType,
    assetClass: AssetClass = AssetClass.FIX,
    extendedTypeId: ExtendedOrderTypeIdentifier,
  ): void {
    let key: string = blotterType + "-" + assetClass + "-" + extendedTypeId;
    this.filterManagerComponents[key] = filterManager;
  }

  public registerFilterView(
    filterView: any,
    blotterType: BlotterType,
    assetClass: AssetClass = AssetClass.FIX,
    extendedTypeId: ExtendedOrderTypeIdentifier,
  ): void {
    let key: string = blotterType + "-" + assetClass + "-" + extendedTypeId;
    this.filterViewComponents[key] = filterView;
  }

  public registerFilterList(
    filterList: any,
    blotterType: BlotterType,
    assetClass: AssetClass = AssetClass.FIX,
    extendedTypeId: ExtendedOrderTypeIdentifier,
  ): void {
    let key: string = blotterType + "-" + assetClass + "-" + extendedTypeId;
    this.filterListComponents[key] = filterList;
  }

  protected getFilterManager(
    blotterType: BlotterType,
    assetClass: AssetClass = AssetClass.FIX,
    extendedTypeId: ExtendedOrderTypeIdentifier,
  ): any {
    let key: string = blotterType + "-" + assetClass + "-" + extendedTypeId;
    return this.filterManagerComponents[key];
  }

  protected getFilterView(
    blotterType: BlotterType,
    assetClass: AssetClass = AssetClass.FIX,
    extendedTypeId: ExtendedOrderTypeIdentifier,
  ): any {
    let key: string = blotterType + "-" + assetClass + "-" + extendedTypeId;
    return this.filterViewComponents[key];
  }

  protected getFilterList(
    blotterType: BlotterType,
    assetClass: AssetClass = AssetClass.FIX,
    extendedTypeId: ExtendedOrderTypeIdentifier,
  ): any {
    let key: string = blotterType + "-" + assetClass + "-" + extendedTypeId;
    return this.filterListComponents[key];
  }

  public editItem(filter: BfxBlotterFilter): void {
    let editedFilter: BfxBlotterFilter = filter;

    if (editedFilter["preload"]) {
      try {
        let condition: any = editedFilter["preload"];
        let preload: any = editedFilter["preload"];
        delete editedFilter["preload"];
        this.loadFilterConditions(condition, filter).then(() => {
          editedFilter = JSON.parse(JSON.stringify(editedFilter));
        });
        editedFilter["preload"] = filter["preload"] = preload;
      } catch (e) {
        logger.out.warn(
          "Failed to read filter: " +
            filter.filterName +
            "[" +
            filter.filterId +
            "]. Possible error in definition.",
          e,
        );
      }
    } else {
      filter.conditions = [];
    }

    let blotterType: BlotterType = filter.filterIdentifier.blotterType;
    let assetClass: AssetClass = filter.filterIdentifier.assetClass;
    let filterViewComponent: any = this.getFilterView(
      blotterType,
      assetClass,
      filter.extendedTypeId,
    );
    filterViewComponent.editedItem = editedFilter;
  }

  public getTableDefinition(
    filterId: BfxBlotterFilterId,
    extendedTypeId: ExtendedOrderTypeIdentifier,
  ): Promise<TableDefinition> {
    let definitionsMapSearchKey =
      filterId.blotterType + "-" + filterId.assetClass + "-" + extendedTypeId;
    if (this.definitionsMap) {
      return new Promise<TableDefinition>((res, rej) => {
        res(this.definitionsMap[definitionsMapSearchKey]);
      });
    }
    return new Promise<TableDefinition>((res, rej) => {
      Promise.all([
        this.ordersDefinition.defineTable(),
        this.tradesDefinition.defineTable(),
        this.interventionDefinition.defineTable(),
        this.clientOrdersDefinition.defineTable(),
        this.algoMonitorDefinition.defineTable(),
        this.riskTradeDefinition.defineTable(),
      ])
        .then(
          (values) => {
            this.definitionsMap = {};
            this.definitionsMap[
              this.ordersDefinition.getBlotterType() +
                "-" +
                this.ordersDefinition.getAssetClass() +
                "-" +
                ExtendedOrderTypeIdentifier.OTHER
            ] = values[0];
            this.definitionsMap[
              this.tradesDefinition.getBlotterType() +
                "-" +
                this.tradesDefinition.getAssetClass() +
                "-" +
                ExtendedOrderTypeIdentifier.OTHER
            ] = values[1];
            this.definitionsMap[
              this.interventionDefinition.getBlotterType() +
                "-" +
                this.interventionDefinition.getAssetClass() +
                "-" +
                ExtendedOrderTypeIdentifier.OTHER
            ] = values[2];
            this.definitionsMap[
              this.clientOrdersDefinition.getBlotterType() +
                "-" +
                this.clientOrdersDefinition.getAssetClass() +
                "-" +
                ExtendedOrderTypeIdentifier.BENCHMARK
            ] = values[3];
            this.definitionsMap[
              this.algoMonitorDefinition.getBlotterType() +
                "-" +
                this.algoMonitorDefinition.getAssetClass() +
                "-" +
                ExtendedOrderTypeIdentifier.ALGO
            ] = values[4];
            this.definitionsMap[
              this.riskTradeDefinition.getBlotterType() +
                "-" +
                this.riskTradeDefinition.getAssetClass() +
                "-" +
                ExtendedOrderTypeIdentifier.OTHER
            ] = values[5];

            res(this.definitionsMap[definitionsMapSearchKey]);
          },
          (rejection) => {
            logger.out.error(
              "ERROR:BfxBlotterFilterService getTableDefinition rejection:",
              rejection,
            );
            rej(null);
          },
        )
        .catch((reason) => {
          logger.out.error("ERROR:BfxBlotterFilterService getTableDefinition reason: ", reason);
          rej(null);
        });
    });
  }

  public static transform(filterEntry: any, userMap: Map<string, string>): BfxBlotterFilter {
    let lastUpdateDate: number = filterEntry.lastUpdateDts.valueOf();
    let lastUpdateDateFormatted = CubiqTS.moment(new Date(lastUpdateDate)).format(
      "DD/MM/YYYY HH:mm:ss",
    );
    let createdBy: string =
      CubiqTS.Long.isLong(filterEntry?.entryUserId) &&
      userMap.has(filterEntry?.entryUserId?.toString())
        ? userMap.get(filterEntry?.entryUserId?.toString()) ?? null
        : null;

    let filter: BfxBlotterFilter | null = null;
    let extendTypeId: ExtendedOrderTypeIdentifier | null = filterEntry.extendedInfo.has(
      EXTENDED_TYPE_PROPERTY_NAME,
    )
      ? filterEntry.extendedInfo.get(EXTENDED_TYPE_PROPERTY_NAME)
      : ExtendedOrderTypeIdentifier.OTHER;
    if (typeof extendTypeId === "string") {
      extendTypeId = Number(extendTypeId);
    }
    if (filterEntry instanceof BlotterDefinitionImpl) {
      let blotterDefinition: BlotterDefinitionImpl = filterEntry as BlotterDefinitionImpl;
      let blotterId: string = blotterDefinition.blotterId.toString();
      filter = new BfxBlotterFilter(
        blotterId,
        blotterDefinition.blotterName,
        createdBy,
        lastUpdateDate,
        lastUpdateDateFormatted,
      );
      filter.queryGroup = filterEntry.queryGroup;
    } else {
      let blotterFilter: BlotterFilterImpl = filterEntry as BlotterFilterImpl;
      let blotterId: string = blotterFilter.filterId.toString();
      filter = new BfxBlotterFilter(
        blotterId,
        blotterFilter.filterName,
        createdBy ?? "N/A",
        lastUpdateDate,
        lastUpdateDateFormatted,
      );
      filter.system = blotterFilter.isSystemFilter;
      filter.queryGroup = blotterFilter.queryGroup;
    }
    filter.extendedTypeId = extendTypeId;
    filter["preload"] = filterEntry;

    return filter;
  }

  protected loadFilterConditions(filterEntry: any, filter: any): Promise<void> {
    let entry: BlotterFilterImpl = filterEntry as BlotterFilterImpl;
    filter.conditions = [];

    let columnDefMap: any = {};
    return new Promise<void>((res, rej) => {
      this.getTableDefinition(
        new BfxBlotterFilterId(entry.blotterType, entry.assetClass),
        filter.extendedTypeId,
      )
        .then(
          (tableDef) => {
            if (tableDef != null) {
              tableDef.columns.forEach((a) => {
                if (a.columnFilterType > 0) {
                  columnDefMap[a.field] = a;
                }
              });

              let queryGroup: QueryGroupImpl = entry.queryGroup as QueryGroupImpl;
              if (queryGroup.condition == Condition.OR) {
                for (let i: number = 0; i < queryGroup.groups.length; i++) {
                  let group: QueryGroupImpl = queryGroup.groups[i];
                  this.loadFilterGroup(
                    filter,
                    group,
                    columnDefMap,
                    entry.blotterType,
                    entry.assetClass,
                  );
                  if (i < queryGroup.groups.length - 1) {
                    filter.conditions.push(
                      new BfxBlotterFilterCondition(null, null, null, null, false, true),
                    );
                  }
                }
              } else {
                this.loadFilterGroup(
                  filter,
                  queryGroup,
                  columnDefMap,
                  entry.blotterType,
                  entry.assetClass,
                );
              }
            }
          },
          (rejection) => {
            logger.out.error(
              "ERROR:BfxBlotterFilterService getTableDefinition rejection:",
              rejection,
            );
          },
        )
        .catch((reason) => {
          logger.out.error("ERROR:BfxBlotterFilterService getTableDefinition reason: ", reason);
        });
    });
  }

  protected loadFilterGroup(
    filter: any,
    group: QueryGroupImpl,
    columnDefMap: any,
    blotterType: BlotterType,
    assetClass: AssetClass,
  ): void {
    for (let i: number = 0; i < group.rules.length; i++) {
      let rule: QueryRuleImpl = group.rules[i];
      let columnDef: ColumnDefinition = columnDefMap[rule.field];
      let values: Array<string> = rule.values;
      let currencyPairGroupProcessing: boolean = false;
      let altValue: Array<string> = null;
      let value: any = null;
      if (columnDef) {
        if (DATE_COLUMN_FILTER_TYPES.indexOf(columnDef.columnFilterType) >= 0) {
          value = values.map((r) => new Date(parseInt(r)));
        } else if (NUMBER_COLUMN_FILTER_TYPES.indexOf(columnDef.columnFilterType) >= 0) {
          value = values.map((r) => Number(r));
        } else if (OBJECT_COLUMN_FILTER_TYPES.indexOf(columnDef.columnFilterType) >= 0) {
          let objectValues: Array<any>;
          let filterViewComponent: any = this.getFilterView(
            blotterType,
            assetClass,
            filter.extendedTypeId,
          );
          objectValues = filterViewComponent.objectValues[columnDef["field"]];
          value = [];
          altValue = [];

          for (let j: number = 0; j < values.length; j++) {
            let valueIndex;
            if (values[j] == CURRENCY_GROUP_VALUE_IDENTIFIER)
              currencyPairGroupProcessing = !currencyPairGroupProcessing;

            if (currencyPairGroupProcessing) {
              valueIndex = this._currencyPairGroupData.findIndex((a) => a.value + "" == values[j]);

              if (valueIndex >= 0) altValue.push(this._currencyPairGroupData[valueIndex]);
            } else {
              valueIndex = objectValues.findIndex((a) => a.value + "" == values[j]);

              if (valueIndex >= 0) value.push(objectValues[valueIndex]);
            }
          }
        } else if (BOOLEAN_COLUMN_FILTER_TYPES.indexOf(columnDef.columnFilterType) >= 0) {
          value = values.map((r) => r === "true");
        } else if (COMPOSITE_COLUMN_FILTER_TYPES.indexOf(columnDef.columnFilterType) >= 0) {
          let displayValues = values.map((rules) => {
            return { value: DIFilterReason.getFilterDisplayValue(rules) };
          });
          value = [...new Map(displayValues.map((item) => [item["value"], item])).values()];
        }

        if (
          values.length == 1 &&
          OBJECT_COLUMN_FILTER_TYPES.indexOf(columnDef.columnFilterType) < 0 &&
          COMPOSITE_COLUMN_FILTER_TYPES.indexOf(columnDef.columnFilterType) < 0
        ) {
          value = values[0];
        }

        let condition: BfxBlotterFilterCondition = new BfxBlotterFilterCondition(
          rule.field,
          columnDef["headerName"],
          rule.operator,
          value,
          false,
          false,
        );

        if (this.betweenOperators[condition.operatorData.value] && value != null) {
          condition["from"] = value[0];
          condition["to"] = value[1];
          condition.value = null;
        }
        if (altValue) {
          condition.altValue = altValue;
        }

        filter.conditions.push(condition);
      }
    }

    filter.conditions.push(new BfxBlotterFilterCondition(null, null, null, null, true, false));
  }

  public setEditMode(
    edit: boolean,
    blotterType: BlotterType,
    assetClass: AssetClass = AssetClass.FIX,
    extendedTypeId: ExtendedOrderTypeIdentifier,
  ): void {
    let filterView: any = this.getFilterView(blotterType, assetClass, extendedTypeId);

    if (filterView != null) {
      filterView.edit = edit;
    } else {
      logger.out.warn(
        "Set edit mode called on a non existing filter view:" + blotterType + " " + assetClass,
      );
    }
  }

  public set currencyPairGroupData(currencyPairGroupData: Array<any>) {
    this._currencyPairGroupData = currencyPairGroupData;
  }

  public onResize(): void {
    this.resizeSubject.next();
  }

  public copyFilterService(
    event,
    selectedFilter: BfxBlotterFilter,
    tableDefinition: TableDefinition,
    type,
    body: Element,
  ): void {
    let service: any =
      type == FilterType.BlotterDefinition ? this.definitionService : this.filterService;
    let field: string = type == FilterType.BlotterDefinition ? "blotterName" : "filterName";
    this.copyFromBlotterType = selectedFilter.filterIdentifier.blotterType;
    this.filterActionSubscription.next(
      new BfxDialogData(
        BfxDialogCommand.DIALOG_FILTER_COPY_OPEN,
        null,
        new BfxBlotterFilterActionEvent(
          selectedFilter,
          tableDefinition,
          this.definitionsMap,
          service,
          field,
        ),
        { blotterName: "OrderBlotter" },
        body,
      ),
    );
  }

  private clearListAndView(
    blotterType: BlotterType,
    assetClass: AssetClass,
    extendedTypeId: ExtendedOrderTypeIdentifier,
  ) {
    let filterViewComponent: any = this.getFilterView(blotterType, assetClass, extendedTypeId);
    let filterListComponent: any = this.getFilterList(blotterType, assetClass, extendedTypeId);

    if (filterViewComponent.editedItem != null && filterListComponent != null) {
      filterViewComponent.editedItem = null;
      filterListComponent.resetListTable();
    }
  }

  public clearView(
    blotterType: BlotterType,
    assetClass: AssetClass,
    extendedTypeId: ExtendedOrderTypeIdentifier,
  ) {
    let filterViewComponent: any = this.getFilterView(blotterType, assetClass, extendedTypeId);
    let filterListComponent: any = this.getFilterList(blotterType, assetClass, extendedTypeId);

    if (filterViewComponent.editedItem != null && filterListComponent != null) {
      filterViewComponent.editedItem = null;
    }
  }

  public get selFilter() {
    return this._selFilter;
  }

  public set selFilter(selFilter: string) {
    this._selFilter = selFilter;
  }

  public listenForFilterDeleting(): Observable<string[]> {
    return this.filtersDeletedSubject.asObservable();
  }

  public setIsEditableMode(isEditableMode: boolean) {
    this.isEditableMode.next(isEditableMode);
  }

  public setIsDialogMode(isDialogOpenMode: boolean) {
    this.isDialogOpenMode.next(isDialogOpenMode);
  }

  public loadOrderTypeDataset(
    keys: Array<string>,
    loadBmarkOrders = false,
    isBmarkOnly: boolean = false,
    algosOnly: boolean = false,
  ): Promise<Array<string | AutoCompleteFilterOrderTypeLabelValue>> {
    return new Promise((res, rej) => {
      let orderTypes: Array<string | AutoCompleteFilterOrderTypeLabelValue> = [];
      let promiseList: Array<Promise<any>> = [];

      promiseList.push(this.systemConfigService.getSystemConfig(SysFeatures.slAtRateEnabled));
      promiseList.push(this.systemConfigService.getSystemConfig(SysFeatures.tSlAtRateEnabled));
      promiseList.push(this.systemConfigService.getSystemConfig(SysFeatures.algosEnabled));
      promiseList.push(this.systemConfigService.getSystemConfig(SysFeatures.benchmarksEnabled));
      promiseList.push(this.systemConfigService.getSystemConfig(SysFeatures.bmRiskRoutingEnabled));
      promiseList.push(this.algoStrategyLookupService.getAll());
      promiseList.push(this.systemConfigService.getSystemConfig(SysFeatures.fixingOrderShortName));
      promiseList.push(this.systemConfigService.getSystemConfig(SysFeatures.marketOrdersEnabled));
      promiseList.push(Promise.resolve(this.algoHedgeConfigService.getAll()));
      promiseList.push(this.systemConfigService.getSystemConfig(SysFeatures.manualRiskRouting));

      if (isBmarkOnly) {
        keys = this.purgeKeysForBenchmarkFilterManager(keys, [
          "BMARK",
          // "BMRT",
          "BMHO",
          "BMALGO",
        ]);
      }

      Promise.all(promiseList).then((values: Array<any>) => {
        let slAtRateEnabled = values[0].toUpperCase() == "Y";
        let tSlAtRateEnabled = values[1].toUpperCase() == "Y";
        let algosEnabled = values[2].toUpperCase() == "Y";
        let benchmarksEnabled = values[3].toUpperCase() == "Y";
        let bmRiskRoutingEnabled = values[4].toUpperCase() == "Y";
        let algoStrategies: Array<CubiqTS.AlgoStrategyImpl> = values[5];
        // Filter algo strategies for TRadable
        const tradableSet = this.activeUser.tradableAlgos;
        const tradableAlgoStrategies = algoStrategies.filter((algo) => tradableSet.has(algo.id));
        let bmarkShortName: string =
          values[6] != null && values[6].length > 0 ? values[6] : BMARK_DEFAULT_NAME;
        let mktOrdersEnabled: boolean = values[7].toUpperCase() == "Y";
        let hedgeConfigs: Array<CubiqTS.AlgoHedgeConfigInfoImpl> = values[8];
        let manualRiskRoutingEnabled = values[9].toUpperCase() == "Y";
        let hedgeStrategies: Array<any> = tradableAlgoStrategies.filter((algo) => {
          // STRATEGY_NAME == algo.id
          return hedgeConfigs.some((hedge) => {
            let tg = this.traderGroupService.getTraderGroup(hedge.traderGroupId);
            let isSupportsBMAlgosFg = false;

            if (tg != null) {
              isSupportsBMAlgosFg = tg.isSupportsBMAlgosFg;
            }

            return isSupportsBMAlgosFg && hedge.enabledFg === true && hedge.orderType === algo.id;
          });
        });

        keys.forEach((type) => {
          switch (type) {
            case "SL_ATRATE":
              if (slAtRateEnabled && !algosOnly) {
                orderTypes.push(type);
              }
              break;
            case "TSL":
              if (tSlAtRateEnabled && !algosOnly) {
                orderTypes.push(type);
              }
              break;
            case "ALGO":
              if (algosEnabled && !isBmarkOnly) {
                if (tradableAlgoStrategies && tradableAlgoStrategies.length > 0) {
                  tradableAlgoStrategies.forEach((algo: CubiqTS.AlgoStrategyImpl) => {
                    orderTypes.push(
                      new AutoCompleteFilterOrderTypeLabelValue(
                        FilterOrderTypes.ALGO,
                        algo.description,
                        algo.description,
                        algo.id,
                      ),
                    );
                  });
                }
              }
              break;
            case "BMALGO":
              if (benchmarksEnabled && algosEnabled && isBmarkOnly) {
                if (hedgeConfigs && hedgeConfigs.length > 0) {
                  hedgeStrategies.forEach((algo: CubiqTS.AlgoStrategyImpl) => {
                    // keep it FilterOrderTypes.ALGO for find handling.
                    orderTypes.push(
                      new AutoCompleteFilterOrderTypeLabelValue(
                        FilterOrderTypes.BMALGO,
                        algo.description,
                        algo.description,
                        algo.id,
                      ),
                    );
                  });
                }
              }
              break;
            case "BMARK":
              if (benchmarksEnabled && (isBmarkOnly || loadBmarkOrders)) {
                orderTypes.push(
                  new AutoCompleteFilterOrderTypeLabelValue(
                    FilterOrderTypes.BMARK,
                    bmarkShortName,
                    CubiqTS.RequestOrderType[CubiqTS.RequestOrderType.BMARK],
                  ),
                );
              }
              break;
            case "BMHO":
              if (
                benchmarksEnabled &&
                (bmRiskRoutingEnabled || manualRiskRoutingEnabled) &&
                (isBmarkOnly || loadBmarkOrders)
              ) {
                orderTypes.push(type);
              }
              break;
            case "MARKET":
            case "MARKET_IOC":
            case "MARKET_FOK": {
              if (mktOrdersEnabled && !algosOnly) {
                orderTypes.push(type);
              }
              break;
            }
            default:
              if (!algosOnly) {
                orderTypes.push(type);
              }
              break;
          }
        });
        res(orderTypes);
      });
    });
  }

  public loadProductDataset(
    keys: Array<string>,
    purgeKeysForBmarkFilterManager = true,
    isBmarkFilterManager: boolean = false,
    primaryTypeName: string = null,
  ): Promise<Array<string>> {
    return new Promise((res, rej) => {
      let products: Array<string> = [];

      if (isBmarkFilterManager && purgeKeysForBmarkFilterManager) {
        keys = this.purgeKeysForBenchmarkFilterManager(keys, ["SPT", "FWD", "SSP", "SWP"]);
      }

      let promiseList: Array<Promise<any>> = [];
      promiseList.push(this.systemConfigService.getSystemConfig(SysFeatures.fwdEnabled));
      promiseList.push(this.systemConfigService.getSystemConfig(SysFeatures.ndfEnabled));
      promiseList.push(this.systemConfigService.getSystemConfig(SysFeatures.benchmarksEnabled));
      Promise.all(promiseList).then((values: Array<any>) => {
        let fwdEnabled = values[0].toUpperCase() == "Y";
        let ndfEnabled = values[1].toUpperCase() == "Y";
        let bmarkEnabled = values[2].toUpperCase() == "Y";

        keys.forEach((type) => {
          switch (type) {
            case "SPT":
              products.push(type);
              break;
            case "FWD":
              if (fwdEnabled) {
                products.push(type);
              }
              break;
            case "NDF":
              if (ndfEnabled) {
                products.push(type);
              }
              break;
            case "SSP":
            case "SWP":
              if (
                bmarkEnabled &&
                (isBmarkFilterManager || primaryTypeName === CubiqTS.OrderDataImpl.enType)
              ) {
                products.push(type);
              }
              break;
          }
        });

        res(products);
      });
    });
  }

  private purgeKeysForBenchmarkFilterManager(
    keys: Array<string>,
    onlyKeysToBeIncluded: Array<string>,
  ): Array<string> {
    return keys.filter((elem) => onlyKeysToBeIncluded.includes(elem));
  }

  private filterOnExtendedTypeId(
    item: BlotterDefinitionImpl,
    extendedOrderTypeId: ExtendedOrderTypeIdentifier,
  ): boolean {
    if (extendedOrderTypeId == null) {
      return true;
    } else if (extendedOrderTypeId != null && item.extendedInfo.has(EXTENDED_TYPE_PROPERTY_NAME)) {
      let itemExtendedType = item.extendedInfo.get(EXTENDED_TYPE_PROPERTY_NAME);
      return (
        ExtendedOrderTypeIdentifier[itemExtendedType] ==
        ExtendedOrderTypeIdentifier[extendedOrderTypeId]
      );
    } else {
      return extendedOrderTypeId === ExtendedOrderTypeIdentifier.OTHER;
    }
  }

  public applyFilterConfig(filterConfig: FilterConfigurationEvent) {
    this.filterConfigurationSubject.next(filterConfig);
  }

  migrateFilterConfiguration(filterConfiguration: FilterConfiguration): FilterConfiguration {
    if (Array.isArray(filterConfiguration)) {
      filterConfiguration = filterConfiguration.map((item) => {
        if (typeof item === "number" && Number.isSafeInteger(item)) {
          return `${item}`;
        }
        return item;
      });
    }
    return filterConfiguration;
  }
}
